import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Typography, Input } from 'antd';
import { ClockCircleOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import './AnirudhBhaiya.css';

const { Title } = Typography;

const STATUS_COLORS = {
  open: 'blue',
  pending: 'orange',
  ready: 'green',
  repair: 'magenta',
  dispatch: 'cyan',
  completed: 'green',
  cancelled: 'red',
  return: 'purple',
};

const formatDate = (val) => {
  if (!val) return '—';
  const d = dayjs(val);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

const daysOverdue = (record) => {
  const today = dayjs().startOf('day');
  if (record.makerGivenDate) {
    const makerDays = today.diff(dayjs(record.makerGivenDate).startOf('day'), 'day');
    if (makerDays > 0) return makerDays;
  }
  if (record.orderDate) {
    const age = today.diff(dayjs(record.orderDate).startOf('day'), 'day');
    if (age > 7) return age - 7;
  }
  return 0;
};

export default function AnirudhBhaiya() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await axios.get('/orders/anirudh-bhaiya', { params });
      setOrders(res.data?.data ?? []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load orders';
      dispatch(setMessage({ message: msg, type: 'error' }));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, dispatch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 120,
      render: (val, record) => (
        <span
          className="anirudh-bhaiya-cell-link"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/orders/${record._id}`)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/orders/${record._id}`); }}
        >
          {val || '—'}
        </span>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (val) => <span className="anirudh-bhaiya-cell-customer">{val || '—'}</span>,
    },
    {
      title: 'Order date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: formatDate,
    },
    {
      title: 'Maker given date',
      dataIndex: 'makerGivenDate',
      key: 'makerGivenDate',
      width: 140,
      render: (val) => <span className="anirudh-bhaiya-date-overdue">{formatDate(val)}</span>,
    },
    {
      title: 'Overdue',
      key: 'overdue',
      width: 90,
      render: (_, record) => {
        const days = daysOverdue(record);
        return (
          <Tag color="red" className="anirudh-bhaiya-overdue-tag">
            {days} {days === 1 ? 'day' : 'days'}
          </Tag>
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'sourceOfOrder',
      key: 'sourceOfOrder',
      width: 110,
      render: (s) => <Tag className="anirudh-bhaiya-tag-source">{s || '—'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => (
        <Tag color={STATUS_COLORS[s] || 'default'} className="anirudh-bhaiya-tag-status">
          {s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'}
        </Tag>
      ),
    },
  ];

  return (
    <div className="anirudh-bhaiya-page">
      <div className="anirudh-bhaiya-header">
        <div className="anirudh-bhaiya-header-left">
          <div className="anirudh-bhaiya-header-title-row">
            <ClockCircleOutlined className="anirudh-bhaiya-header-icon" />
            <Title level={3} style={{ margin: 0 }}>
              Anirudh Bhaiya
            </Title>
            {!loading && orders.length > 0 && (
              <Tag color="orange" className="anirudh-bhaiya-count-badge">
                {orders.length}
              </Tag>
            )}
          </div>
          <Input
            allowClear
            placeholder="Search order # or customer"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="anirudh-bhaiya-search-input"
          />
        </div>
        <p className="anirudh-bhaiya-subtitle">
          Open orders where the maker given date has passed, or the order date is older than 7 days.
        </p>
      </div>

      <Card className="anirudh-bhaiya-card">
        <Table
          className="anirudh-bhaiya-table"
          dataSource={orders.map((row) => ({ ...row, key: row._id }))}
          columns={columns}
          loading={loading}
          rowClassName="anirudh-bhaiya-row"
          tableLayout="auto"
          pagination={{ pageSize: 20, showTotal: (t) => `${t} orders` }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
}
